/**
 * Subscription access helpers.
 *
 * Businesses without a `subscription` field are grandfathered (full access).
 * New signups get subscription.status = "trialing" with a trial period.
 * Admin controls access by setting status to "active" or "inactive" in
 * Firestore. Trial period (trialDays) and start time (trialStartedAt) are
 * stored on the subscription object and can be adjusted in Firestore.
 */

/**
 * @param {object|Date|string|number|null|undefined} value
 * @return {Date|null}
 */
function toJsDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (value.seconds) return new Date(value.seconds * 1000);
  return null;
}

/**
 * Returns the trial end Date for a subscription, or null if not trialing.
 * @param {object|null|undefined} subscription
 * @return {Date|null}
 */
export function getTrialEndDate(subscription) {
  if (!subscription) return null;
  const start = toJsDate(subscription.trialStartedAt);
  if (!start) return null;
  const days = Number(subscription.trialDays);
  if (!days || Number.isNaN(days)) return null;
  return new Date(start.getTime() + days * 24 * 60 * 60 * 1000);
}

/**
 * True while the business is within its trial window.
 * @param {object|null|undefined} subscription
 * @return {boolean}
 */
export function isInTrial(subscription) {
  if (subscription?.status !== 'trialing') return false;
  const end = getTrialEndDate(subscription);
  if (!end) return true; // no end date set → treat as still in trial
  return end.getTime() > Date.now();
}

/**
 * @param {object|null|undefined} businessInfo
 * @return {boolean}
 */
export function isSubscriptionAccessAllowed(businessInfo) {
  if (!businessInfo) return false;
  const subscription = businessInfo.subscription;
  // Grandfathered: no subscription object → full access
  if (!subscription) return true;
  if (subscription.status === 'active') return true;
  if (subscription.status === 'trialing' && isInTrial(subscription)) return true;
  return false;
}

/**
 * Human-readable status label.
 * @param {object|null|undefined} subscription
 * @return {string}
 */
export function subscriptionStatusLabel(subscription) {
  if (!subscription) return 'Legacy (full access)';
  const status = subscription.status;
  if (status === 'active') return 'Active';
  if (status === 'trialing') {
    const end = getTrialEndDate(subscription);
    if (!end) return 'Trial';
    if (end.getTime() > Date.now()) {
      const daysLeft = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      return `Trial (${daysLeft} day${daysLeft === 1 ? '' : 's'} left)`;
    }
    return 'Trial expired';
  }
  if (status === 'inactive') return 'Inactive';
  return status ? String(status) : 'Unknown';
}

/**
 * Format a Firestore Timestamp, Date, or ISO string for display.
 * @param {object|Date|string|null|undefined} value
 * @return {string|null}
 */
export function formatSubscriptionDate(value) {
  const date = toJsDate(value);
  if (!date) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
